import { useState, useEffect, useMemo } from 'react';
import { Timer, Play, Pause, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToolUsage } from '@/hooks/useToolUsage';

const CountdownTimer = () => {
  useToolUsage('countdown-timer');
  const [targetDate, setTargetDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 16);
  });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const diff = useMemo(() => {
    const target = new Date(targetDate).getTime();
    const ms = Math.max(0, target - now);
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return { days, hours, minutes, seconds, expired: ms === 0 };
  }, [targetDate, now]);

  const units = [
    { label: 'Days', value: diff.days },
    { label: 'Hours', value: diff.hours },
    { label: 'Minutes', value: diff.minutes },
    { label: 'Seconds', value: diff.seconds },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Timer className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Countdown Timer</h1>
          <p className="text-muted-foreground">Set a target date and watch the countdown</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="max-w-sm">
            <Label className="text-sm mb-2 block">Target Date & Time</Label>
            <Input type="datetime-local" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {units.map(u => (
              <div key={u.label} className="text-center p-6 rounded-xl bg-muted/50 border border-border">
                <p className="text-4xl sm:text-5xl font-bold text-primary tabular-nums">
                  {String(u.value).padStart(2, '0')}
                </p>
                <p className="text-sm text-muted-foreground mt-2">{u.label}</p>
              </div>
            ))}
          </div>

          {diff.expired && (
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <p className="text-lg font-bold text-primary">🎉 Time's up!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CountdownTimer;
